// TechStops (Domain/Schedule; was ScheduleViews' TechDay, BL-189): one technician's day as a list of
// stops: their pen and name, the day in a sentence (their hours, how many visits, booked and still
// open), then each stop in order: its time, a node (filled while they're on it, red-ringed when late,
// dashed for open time, greyed when done or away) and what it is, with its state as a word. A day off
// says only why. Not the board's live card (TechDayCard): this is the day read through, for Team
// manager's Who's working. Draws from ready-to-show words (schedule-schemas.ts' TechDayProps). Reads only
// --techstops-*; PenKey reads its own.
import type { TechDayProps } from '../../schedule-schemas.ts';
import { PenKey } from '../PenKey/PenKey.tsx';
import type { Pen } from '../pen.ts';
import { duration, visitsWord } from '../../format.ts';
import './TechStops.css';

type Stop = TechDayProps['stops'][number];

/** "Today, Friday, October 23, works 8 a.m.–4:30 p.m. 2 visits, 5 h 30 booked, 3 h still open." */
export function daySentence(d: TechDayProps): string {
  const head = `${d.date}${d.hours ? `, works ${d.hours}` : ''}`;
  if (d.off || !d.stops.length) return head;
  const visits = d.stops.filter((s) => s.kind === 'visit').length;
  const rest = `${visitsWord(visits)}, ${duration(d.bookedMinutes)} booked, ${d.openMinutes ? `${duration(d.openMinutes)} still open` : 'nothing open'}.`;
  return `${head}${/\.$/.test(head) ? '' : '.'} ${rest}`;
}

const STATE_WORD: Record<NonNullable<Stop['state']>, string> = {
  done: 'Done',
  paused: 'Paused',
  now: 'Working now',
  next: 'Booked',
};

export function TechStops({ data: d }: { data: TechDayProps }) {
  return (
    <article className="fl-techstops" aria-label={`${d.name}’s day`}>
      <p className="fl-techstops__who">
        <PenKey pen={(d.pen ?? null) as Pen | null}>
          <b className="fl-techstops__name">{d.name}</b>
        </PenKey>
      </p>
      <p className="fl-techstops__sub">{daySentence(d)}</p>
      {d.off ? (
        <p className="fl-techstops__sub">Off: {d.off}</p>
      ) : !d.stops.length ? (
        <p className="fl-techstops__empty">Nothing booked: {d.name} is free all day.</p>
      ) : (
        <ol className="fl-techstops__stops">
          {d.stops.map((s, i) => (
            <li
              key={`${s.kind}-${s.start}-${i}`}
              className="fl-techstops__stop"
              data-kind={s.kind}
              data-state={s.state ?? undefined}
              data-late={s.late ? '' : undefined}
            >
              <span className="fl-techstops__time">
                {s.start}
                <small>{s.end}</small>
              </span>
              <svg className="fl-techstops__node" viewBox="0 0 14 14" aria-hidden="true">
                <circle cx="7" cy="7" r="6" />
              </svg>
              <span>
                <b className="fl-techstops__what">{s.kind === 'open' ? `Open, until ${s.end}` : s.title}</b>
                {s.kind === 'visit' && (
                  <span
                    className="fl-techstops__word"
                    data-kind={s.state === 'now' ? 'now' : s.state === 'paused' ? 'wait' : 'muted'}
                  >
                    {' '}
                    {STATE_WORD[s.state ?? 'next']}
                  </span>
                )}
                {s.detail && <span className="fl-techstops__sub">{s.detail}</span>}
                {s.late && (
                  <span className="fl-techstops__sub">
                    <span className="fl-techstops__word" data-kind="bad">
                      {s.late}
                    </span>
                  </span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}
